import React from 'react';
import { useLingui } from '@lingui/react/macro';

import AlertInfo from '@rover/icons/svg/alert-info.svg';
import { Box, Button, Flex, Text } from '@rover/kibble/core';
import useI18nContext from '@rover/react-lib/src/context/useI18nContext';
import type { EnhancedPersonSitterProfileServiceAddOn as AddOn } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfileServiceAddOn';
import useIsBHUser from '@rover/shared/js/hooks/useIsBHUser';
import type { PriceTransparencyLevel } from '@rover/types/src/PriceTransparencyLevel';
import { ServiceAddOnTypeSlugs as AddOnSlugs } from '@rover/types/src/ServiceAddOn';
import { getIsPriceTransparencyFull } from '@rover/utilities/priceTransparency';

import { formatPrice, hasSecondaryText } from './utils';

// Rates are grouped by the pet they price, each with the variants that belong
// under it; anything the owner pays on top of a rate is listed apart.
const RATE_GROUPS = [
  { parent: AddOnSlugs.standardRate, children: [AddOnSlugs.additionalDog, AddOnSlugs.puppy] },
  { parent: AddOnSlugs.catCare, children: [AddOnSlugs.additionalCat] },
];
const STANDALONE_RATE_SLUGS = [
  AddOnSlugs.holidayRate,
  AddOnSlugs.extendedStay,
  AddOnSlugs.shortNotice,
];
const RATE_SLUGS = [
  ...RATE_GROUPS.flatMap(({ parent, children }) => [parent, ...children]),
  ...STANDALONE_RATE_SLUGS,
];

const CAT_RATE_SLUGS: string[] = [AddOnSlugs.additionalCat, AddOnSlugs.catCare];

type Props = {
  rates: AddOn[];
  acceptCats: boolean;
  onMoreInfoClick?: (addOn: AddOn) => void;
  priceTransparencyLevel?: PriceTransparencyLevel;
  showSittersRates?: boolean;
};

const ServiceRatesBreakdown = ({
  rates,
  acceptCats,
  onMoreInfoClick,
  priceTransparencyLevel,
  showSittersRates = true,
}: Props): JSX.Element => {
  const { t, i18n } = useLingui();
  const { locale } = useI18nContext();
  const isBHUser = useIsBHUser();

  const visibleRates = rates.filter((addOn) => acceptCats || !CAT_RATE_SLUGS.includes(addOn.slug));
  const bySlug = (slug: string): AddOn | undefined =>
    visibleRates.find((addOn) => addOn.slug === slug);

  // The names the API returns spell out the service ("Additional Dog Rate"); the
  // breakdown reads better with the pet on its own line.
  const getLabel = (addOn: AddOn): string => {
    switch (addOn.slug) {
      case AddOnSlugs.additionalDog:
        return t`Additional Dogs`;
      case AddOnSlugs.puppy:
        return t`Puppy`;
      case AddOnSlugs.additionalCat:
        return t`Additional Cats`;
      case AddOnSlugs.holidayRate:
        return t`Peak dates`;
      case AddOnSlugs.extendedCare:
        return t`Extended care`;
      case AddOnSlugs.bathingGrooming:
        return t`Bathing / grooming`;
      case AddOnSlugs.pickUpDropOff:
        return t`Sitter pick-up and drop-off`;
      default:
        return addOn.nameDesc;
    }
  };

  const renderRow = (addOn: AddOn, isNested = false): JSX.Element => {
    const price = formatPrice(
      i18n,
      addOn,
      locale,
      getIsPriceTransparencyFull(priceTransparencyLevel) || isBHUser,
      !showSittersRates && isBHUser ? addOn.priceInCredits : null,
      false
    );

    return (
      <Flex
        key={addOn.slug}
        justifyContent="space-between"
        alignItems="start"
        gap="4x"
        pt="4x"
        pl={isNested ? '4x' : '0x'}
        data-testid={`service-rate-${addOn.slug}`}
      >
        <Flex alignItems="center">
          <Text size="200">{getLabel(addOn)}</Text>
          {addOn.helpText && (
            <Button
              variant="flat"
              onPress={() => onMoreInfoClick?.(addOn)}
              p="0x"
              ml="2x"
              data-testid="help-text-button"
              aria-label={t`More information`}
              sx={{ border: 0 }}
            >
              <AlertInfo width={16} height={16} />
            </Button>
          )}
        </Flex>
        <Flex
          flexDirection="column"
          alignItems="end"
          textAlign="right"
          webProps={{ role: 'group' }}
        >
          <Text size="200" fontWeight="semibold">
            {price}
          </Text>
          {hasSecondaryText(addOn) && (
            <Text size="100" textColor="secondary">
              {addOn.unitAsSentence}
            </Text>
          )}
        </Flex>
      </Flex>
    );
  };

  const groupedRates = RATE_GROUPS.flatMap(({ parent, children }) => {
    const parentRate = bySlug(parent);
    if (!parentRate) return [];
    return [
      renderRow(parentRate),
      ...children.flatMap((slug) => {
        const child = bySlug(slug);
        return child ? [renderRow(child, true)] : [];
      }),
    ];
  });

  const standaloneRates = STANDALONE_RATE_SLUGS.flatMap((slug) => {
    const rate = bySlug(slug);
    return rate ? [renderRow(rate)] : [];
  });

  const addOns = visibleRates.filter((addOn) => !RATE_SLUGS.includes(addOn.slug as AddOnSlugs));

  return (
    <Flex flexDirection="column" pt="2x" data-testid="service-rates-breakdown">
      {groupedRates}
      {standaloneRates}
      {addOns.length > 0 && (
        <>
          <Box pt="6x">
            <Text size="100" fontWeight="semibold" sx={{ textTransform: 'uppercase' }}>
              {t`Add-ons`}
            </Text>
          </Box>
          {addOns.map((addOn) => renderRow(addOn))}
        </>
      )}
    </Flex>
  );
};

export default ServiceRatesBreakdown;
